import {
  ConfirmationBox,
  Icon,
  IconName,
  TextInput,
} from '@infotech-soe/erp-ui-kit';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationMessageType } from '../../../services/commands/UserInteractor';

import { CommandConfirmationDialogProps } from './CommandConfirmationDialogContainer';
import { useStyles } from './styles';

type Props = Omit<
  CommandConfirmationDialogProps,
  | 'type'
  | 'commandCaption'
  | 'dataset'
  | 'icon'
  | 'longMessage'
  | 'shortMessage'
> & {
  moveInCount?: number;
  moveInNodeName?: string;
  renameName?: string;
};

const messages = defineMessages({
  title: {
    id: 'core.confirmation.title',
    defaultMessage: 'Confirm action',
  },
  [ConfirmationMessageType.DELETE_CATALOG]: {
    id: 'core.category.tree.catalogs.delete.short',
    defaultMessage: 'Delete the catalog?',
  },
  [`${ConfirmationMessageType.DELETE_CATALOG}Long`]: {
    id: 'core.category.tree.catalogs.delete.long',
    defaultMessage: 'This will delete the selected catalog.',
  },
  [`${ConfirmationMessageType.CREATE_CATALOG}Title`]: {
    id: 'core.category.tree.catalogs.create.title',
    defaultMessage: 'Create catalog',
  },
  [ConfirmationMessageType.CREATE_CATALOG]: {
    id: 'core.category.tree.catalogs.create.question',
    defaultMessage: 'Catalog name:',
  },
  [`${ConfirmationMessageType.RENAME_CATALOG}Title`]: {
    id: 'core.category.tree.catalogs.rename.title',
    defaultMessage: 'Rename catalog',
  },
  [ConfirmationMessageType.RENAME_CATALOG]: {
    id: 'core.category.tree.catalogs.rename.question',
    defaultMessage: 'Catalog name:',
  },
  [ConfirmationMessageType.MOVE_CATALOG]: {
    id: 'core.category.tree.catalogs.move.short',
    defaultMessage: 'Move catalog?',
  },
  [`${ConfirmationMessageType.MOVE_CATALOG}Long`]: {
    id: 'core.category.tree.catalogs.move.long',
    defaultMessage: 'The catalog will be moved to the new parent.',
  },
  [ConfirmationMessageType.MOVE_IN_CATALOG]: {
    id: 'core.category.tree.catalogs.moveIn.short',
    defaultMessage: 'Move record(s)?',
  },
  [`${ConfirmationMessageType.MOVE_IN_CATALOG}Long`]: {
    id: 'core.category.tree.catalogs.moveIn.long',
    defaultMessage:
      'The {count, plural, one {record} other {records}} will be moved to the directory "{nodeName}"',
  },
  [ConfirmationMessageType.MOVE_HIERARCHY]: {
    id: 'core.category.tree.hierarchy.move.short',
    defaultMessage: 'Move record(s)?',
  },
  [`${ConfirmationMessageType.MOVE_HIERARCHY}Long`]: {
    id: 'core.category.tree.hierarchy.move.long',
    defaultMessage:
      'The {count, plural, one {record} other {records}} will be moved to the structure element "{nodeName}"',
  },
});

const DEFAULT_ICONS: Record<string, IconName> = {
  [ConfirmationMessageType.CREATE_CATALOG]: 'create',
  [ConfirmationMessageType.DELETE_CATALOG]: 'delete',
  [ConfirmationMessageType.RENAME_CATALOG]: 'edit',
  [ConfirmationMessageType.MOVE_CATALOG]: 'move',
  [ConfirmationMessageType.MOVE_IN_CATALOG]: 'move',
  [ConfirmationMessageType.MOVE_HIERARCHY]: 'move',
};

const CatalogsConfirmationDialog = (props: Props): React.JSX.Element => {
  const {
    messageType,
    moveInCount,
    moveInNodeName,
    renameName,
    open,
    onCancel,
    onConfirm,
  } = props;
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [answer, setAnswer] = useState(renameName ?? '');
  const [submitForbidden, setSubmitForbidden] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleConfirm = useCallback(() => {
    onConfirm(messageType, answer);
  }, [answer, messageType, onConfirm]);

  const needAnswer = useMemo(
    () =>
      messageType === ConfirmationMessageType.CREATE_CATALOG ||
      messageType === ConfirmationMessageType.RENAME_CATALOG,
    [messageType]
  );

  useEffect(() => {
    setSubmitForbidden(!answer);
  }, [answer]);

  useEffect(() => {
    if (!open || !needAnswer) return;

    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, needAnswer]);

  return (
    <ConfirmationBox
      className={classes.dialog}
      height="auto"
      modalTrigger={() => document.querySelector('.active-panel.active')}
      open={open}
      submitForbidden={needAnswer ? submitForbidden : false}
      title={formatMessage(
        needAnswer ? messages[`${messageType}Title`] : messages.title
      )}
      width="auto"
      onCancel={onCancel}
      onClose={onCancel}
      onConfirm={handleConfirm}
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.icon}>
            <Icon name={DEFAULT_ICONS[messageType]} size="3em" />
          </div>
          <div className={classes.shortMessage}>
            {formatMessage(messages[messageType])}
          </div>
        </div>
        <div className={classes.longMessage}>
          {needAnswer ? (
            <TextInput
              ref={inputRef}
              required
              value={answer}
              onChange={setAnswer}
              onSubmit={handleConfirm}
              onValidate={(errors: number) => setSubmitForbidden(errors !== 0)}
            />
          ) : (
            formatMessage(messages[`${messageType}Long`], {
              count: moveInCount,
              nodeName: moveInNodeName,
            })
          )}
        </div>
      </div>
    </ConfirmationBox>
  );
};

export default memo(CatalogsConfirmationDialog);
